import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';
import {
  ChecklistSectionHttpCreateRequestModel,
  ChecklistSectionHttpDeleteRequestModel,
  ChecklistSectionHttpEditRequestModel,
  ChecklistSectionHttpReorderRequestModel,
  ChecklistSectionHttpRequestModel,
} from './types';
import { Fetcher } from '../fetcher';
import { ChecklistSectionInDto } from 'shared/dtos/in/template';
import { HttpRequestModelType } from 'shared/domain/httpRequest/httpRequestModel';
import { ReorderChecklistSectionOutDto } from 'shared/dtos/out/checklistSection';

export class ChecklistSectionRequestRunner implements IHttpRequestRunner {
  constructor(private fetcher: typeof Fetcher) {}

  async execute(
    request: ChecklistSectionHttpRequestModel
  ): Promise<ChecklistSectionInDto | void> {
    if (request.entityType === HttpRequestModelType.checklistSectionDocument) {
      return this.deleteSectionDocument(request);
    }

    switch (request.method) {
      case 'POST': {
        return this.createSection(
          request as ChecklistSectionHttpCreateRequestModel
        );
      }
      case 'PUT': {
        if (this.isReorderRequest(request)) {
          return this.reorderSection(
            request as ChecklistSectionHttpReorderRequestModel
          );
        }
        return this.updateSection(
          request as ChecklistSectionHttpEditRequestModel
        );
      }
      case 'DELETE': {
        return this.deleteSection(
          request as ChecklistSectionHttpDeleteRequestModel
        );
      }
    }
  }

  private isReorderRequest(
    request: ChecklistSectionHttpRequestModel
  ): request is ChecklistSectionHttpReorderRequestModel {
    return (
      request.method === 'PUT' && request.url.includes('/reorder')
    );
  }

  private async createSection(
    request: ChecklistSectionHttpCreateRequestModel
  ): Promise<ChecklistSectionInDto> {
    const response = await this.fetcher.POST(
      request.url,
      request.data.checklistSectionCreateOutDto
    );
    return response as ChecklistSectionInDto;
  }

  private async updateSection(
    request: ChecklistSectionHttpEditRequestModel
  ): Promise<ChecklistSectionInDto> {
    const response = await this.fetcher.PUT(
      request.url,
      request.data.checklistSectionUpdateOutDto
    );
    return response as ChecklistSectionInDto;
  }

  private async deleteSection(
    request: ChecklistSectionHttpDeleteRequestModel
  ): Promise<ChecklistSectionInDto> {
    const response = await this.fetcher.DELETE(request.url);
    return response as ChecklistSectionInDto;
  }

  private async reorderSection(
    request: ChecklistSectionHttpReorderRequestModel
  ): Promise<ChecklistSectionInDto> {
    const body: ReorderChecklistSectionOutDto = {
      beforeId: request.data.beforeId,
      afterId: request.data.afterId,
      parentSectionId: request.data.parentSectionId,
    };
    const response = await this.fetcher.PUT(request.url, body);
    return response as ChecklistSectionInDto;
  }

  private async deleteSectionDocument(
    request: ChecklistSectionHttpRequestModel
  ): Promise<void> {
    await this.fetcher.DELETE(request.url);
  }
}
